<template>
  <section class="pv-hero" :class="{ 'has-cover': !!coverUrl }">
    <!--
      The cover sits behind the identity block rather than above it, so the header
      stays one object. A scrim keeps the white text legible over any photo.
    -->
    <div v-if="coverUrl" class="pv-cover" :style="{ backgroundImage: `url(${coverUrl})` }"></div>

    <div class="pv-hero-inner">
      <!--
        The logo is uploaded through the overlay on this tile, so this is where a
        "Company logo" link from the completion checklist has to land.
      -->
      <div class="pv-logo" data-profile-card="logo">
        <div v-if="isLogoLoading" class="pv-logo-frame pv-logo-frame--loading">
          <span class="spinner-border spinner-border-sm"></span>
        </div>
        <img v-else :src="logoUrl || defaultAvatar" class="pv-logo-frame" alt="" />

        <button
          v-if="!isReadOnly"
          type="button"
          class="pv-logo-edit"
          :title="$t('profile.picture.change')"
          :disabled="isUploading"
          @click="triggerFileInput"
        >
          <span v-if="isUploading" class="spinner-border spinner-border-sm"></span>
          <i v-else class="ph-duotone ph-camera"></i>
        </button>

        <input
          v-if="!isReadOnly"
          ref="fileInput"
          type="file"
          class="d-none"
          accept="image/*"
          @change="handleFileSelected"
        />
      </div>

      <div class="pv-identity">
        <div class="pv-identity-top">
          <h1 class="pv-name">{{ displayName }}</h1>
          <span class="pv-badge"><i class="ph-duotone ph-package"></i>{{ $t('profile.role.supplier') }}</span>
          <span v-if="isReadOnly" class="pv-badge pv-badge--muted">
            <i class="ph-duotone ph-eye"></i>{{ $t('profile.readOnly') }}
          </span>
        </div>

        <div class="pv-meta">
          <span v-if="location"><i class="ph-duotone ph-map-pin"></i>{{ location }}</span>
          <span><i class="ph-duotone ph-anchor"></i>{{ $t('profile.hero.portsServed', { count: counts.ports }) }}</span>
          <span v-if="joinedLabel"><i class="ph-duotone ph-clock-counter-clockwise"></i>{{ joinedLabel }}</span>
        </div>

        <div class="pv-rating">
          <span class="pv-stars">
            <i v-for="star in stars" :key="star.index" class="ph-duotone" :class="star.icon"></i>
          </span>
          <span class="pv-rating-value">{{ ratingLabel }}</span>
          <span class="pv-rating-note">
            · {{ $t('profile.hero.reviewCount', { count: reviewTotalCount || 0 }) }}
          </span>
        </div>
      </div>

      <!--
        The cover controls sit in this row rather than pinned to the corner: absolutely
        positioned they landed on top of Share and Edit profile at every width.
      -->
      <div class="pv-actions">
        <template v-if="!isReadOnly">
          <button type="button" class="pv-cover-btn" :disabled="isCoverUploading" @click="triggerCoverInput">
            <span v-if="isCoverUploading" class="spinner-border spinner-border-sm"></span>
            <i v-else class="ph-duotone ph-image"></i>
            {{ coverUrl ? $t('profile.cover.change') : $t('profile.cover.add') }}
          </button>
          <button
            v-if="coverUrl"
            type="button"
            class="pv-cover-btn pv-cover-btn--icon"
            :disabled="isCoverUploading"
            :title="$t('profile.cover.remove')"
            :aria-label="$t('profile.cover.remove')"
            @click="removeCover"
          >
            <i class="ph-duotone ph-trash"></i>
          </button>
          <input
            ref="coverInput"
            type="file"
            class="d-none"
            accept="image/*"
            @change="handleCoverSelected"
          />
        </template>
        <slot name="actions"></slot>
      </div>
    </div>

    <nav class="pv-tabs" :aria-label="$t('profile.tabs.overview')">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="pv-tab"
        :class="{ 'is-active': modelValue === tab.key }"
        :aria-pressed="modelValue === tab.key"
        @click="emit('update:modelValue', tab.key)"
      >
        <i class="ph-duotone" :class="tab.icon"></i>{{ tab.label }}
        <span v-if="tab.count != null" class="pv-tab-count">{{ tab.count }}</span>
      </button>
    </nav>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import fileManagementService from '@/services/fileManagementService';
import { useDateFormatter } from '@/composables/useDateFormatter';
import defaultAvatarSrc from '@/assets/images/logo/vesproc40x40.png';

const props = defineProps({
  /** Active tab key: overview | categories | reviews | certificates | account. */
  modelValue: { type: String, default: 'overview' },
  displayName: { type: String, default: '' },
  location: { type: String, default: '' },
  joinedAt: { type: [String, Date], default: null },
  companyLogoId: { type: String, default: '' },
  coverImageId: { type: String, default: '' },
  isReadOnly: { type: Boolean, default: false },
  targetTenantId: { type: String, default: '' },
  counts: {
    type: Object,
    default: () => ({ ports: 0, categories: 0, certificates: 0 })
  },
  /** Average score on the API's 1–10 scale; null until the summary lands. */
  reviewAverageScore: { type: Number, default: null },
  reviewTotalCount: { type: Number, default: null }
});

const emit = defineEmits(['update:modelValue', 'profile-data-changed', 'cover-changed']);

const store = useStore();
const { t } = useI18n();
const { formatDate } = useDateFormatter();

const fileInput = ref(null);
const logoUrl = ref('');
const isLogoLoading = ref(true);
const isUploading = ref(false);

const coverInput = ref(null);
const coverUrl = ref('');
const isCoverUploading = ref(false);

const MAX_LOGO_SIZE = 5 * 1024 * 1024;
const MAX_COVER_SIZE = 5 * 1024 * 1024;
const COVER_TABLE_NAME = 'TenantCovers';
const defaultAvatar = defaultAvatarSrc;

const tabs = computed(() => [
  { key: 'overview', label: t('profile.tabs.overview'), icon: 'ph-squares-four', count: null },
  {
    key: 'categories',
    label: t('profile.sections.categories'),
    icon: 'ph-list-tree',
    count: props.counts.categories ?? 0
  },
  {
    key: 'reviews',
    label: t('profile.reviews.title'),
    icon: 'ph-star',
    count: props.reviewTotalCount
  },
  {
    key: 'certificates',
    label: t('profile.sections.certifications'),
    icon: 'ph-certificate',
    count: props.counts.certificates ?? 0
  },
  { key: 'account', label: t('profile.tabs.account'), icon: 'ph-identification-card', count: null }
]);

/** The API scores out of 10; the star row is out of 5. */
const ratingOutOfFive = computed(() =>
  props.reviewAverageScore == null ? 0 : props.reviewAverageScore / 2
);

const stars = computed(() =>
  [1, 2, 3, 4, 5].map(index => ({
    index,
    icon: index <= Math.round(ratingOutOfFive.value) ? 'ph-star-fill' : 'ph-star'
  }))
);

const ratingLabel = computed(() =>
  props.reviewAverageScore == null ? t('profile.hero.notRated') : `${ratingOutOfFive.value.toFixed(2)}/5`
);

const joinedLabel = computed(() => {
  if (!props.joinedAt) return '';
  const date = new Date(props.joinedAt);
  if (Number.isNaN(date.getTime())) return '';
  // The user's configured date format, not a locale hardcoded here.
  return t('profile.hero.joined', { date: formatDate(date) });
});

const createdBlobUrls = [];

const loadLogo = async () => {
  isLogoLoading.value = true;
  try {
    const response = await fileManagementService.getProfilePicture(props.targetTenantId || null);
    if (response.success && response.blobUrl) {
      if (logoUrl.value && logoUrl.value !== defaultAvatar) {
        URL.revokeObjectURL(logoUrl.value);
      }
      logoUrl.value = response.blobUrl;
      createdBlobUrls.push(response.blobUrl);
    } else {
      logoUrl.value = defaultAvatar;
    }
  } catch (error) {
    console.error('Error loading company logo:', error);
    logoUrl.value = defaultAvatar;
  } finally {
    isLogoLoading.value = false;
  }
};

const triggerFileInput = () => {
  if (props.isReadOnly) return;
  fileInput.value?.click();
};

const handleFileSelected = async (event) => {
  if (props.isReadOnly) return;

  const file = event.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    store.dispatch('notify', { message: t('profile.picture.invalidType'), type: 'error' });
    return;
  }

  if (file.size > MAX_LOGO_SIZE) {
    store.dispatch('notify', { message: t('profile.picture.sizeError'), type: 'error' });
    return;
  }

  isUploading.value = true;
  try {
    const response = await fileManagementService.uploadProfilePicture(file);
    if (!response.success) throw new Error('Upload failed');

    store.dispatch('notify', { message: t('profile.picture.uploadSuccess'), type: 'success' });
    await loadLogo();

    // The logo counts toward profile completion, so the score is recomputed too —
    // not just the image.
    emit('profile-data-changed');

    // Lets the header avatar refresh without this component knowing about it.
    window.dispatchEvent(new CustomEvent('profile-picture-updated'));
  } catch (error) {
    console.error('Error uploading company logo:', error);
    store.dispatch('notify', { message: t('profile.picture.uploadError'), type: 'error' });
  } finally {
    isUploading.value = false;
    if (event.target) event.target.value = '';
  }
};

/*
  The cover is an ordinary uploaded file; the tenant keeps its id in extra properties.
  Loading it here rather than in the parent keeps the header responsible for its own
  imagery, including revoking the blob when it is replaced.
*/
const loadCover = async () => {
  if (!props.coverImageId) {
    if (coverUrl.value) URL.revokeObjectURL(coverUrl.value);
    coverUrl.value = '';
    return;
  }

  try {
    const response = await fileManagementService.getFileBlob(props.coverImageId);
    if (!response?.success) return;

    if (coverUrl.value) URL.revokeObjectURL(coverUrl.value);
    coverUrl.value = URL.createObjectURL(response.blob);
    createdBlobUrls.push(coverUrl.value);
  } catch (error) {
    console.error('Error loading cover image:', error);
  }
};

const triggerCoverInput = () => {
  if (props.isReadOnly) return;
  coverInput.value?.click();
};

const handleCoverSelected = async (event) => {
  if (props.isReadOnly) return;

  const file = event.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    store.dispatch('notify', { message: t('profile.picture.invalidType'), type: 'error' });
    return;
  }

  if (file.size > MAX_COVER_SIZE) {
    store.dispatch('notify', { message: t('profile.cover.sizeError'), type: 'error' });
    return;
  }

  isCoverUploading.value = true;
  try {
    const prepared = await fileManagementService.prepareFileUploadDtos(
      [file],
      props.targetTenantId,
      COVER_TABLE_NAME
    );
    const uploaded = await fileManagementService.uploadFilesJsonAsync(
      props.targetTenantId,
      prepared,
      COVER_TABLE_NAME
    );

    const fileId = uploaded?.files?.[0]?.id;
    if (!fileId) throw new Error('Upload returned no file id');

    // The parent owns the tenant's extra properties, so it does the persisting.
    emit('cover-changed', fileId);
  } catch (error) {
    console.error('Error uploading cover image:', error);
    store.dispatch('notify', { message: t('profile.cover.uploadError'), type: 'error' });
  } finally {
    isCoverUploading.value = false;
    if (event.target) event.target.value = '';
  }
};

const removeCover = () => {
  if (props.isReadOnly) return;
  emit('cover-changed', '');
};

onMounted(() => {
  loadLogo();
  loadCover();
});

watch(() => props.coverImageId, loadCover);

watch(() => props.companyLogoId, (next, previous) => {
  if (next !== previous) loadLogo();
});

onBeforeUnmount(() => {
  createdBlobUrls.forEach(url => {
    if (url && url !== defaultAvatar) {
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error revoking blob URL:', error);
      }
    }
  });
  logoUrl.value = '';
  coverUrl.value = '';
});
</script>

<style scoped>
/*
  The hero carries the brand's navy with the accent bleeding in from the top-left,
  so the identity block reads as a header rather than as another card.
*/
.pv-hero {
  position: relative;
  background: var(--vp-gray-800);
  background-image: radial-gradient(1100px 380px at 12% -40%, rgba(var(--vp-accent-rgb), 0.42), transparent 60%);
  color: #fff;
  padding: 26px 22px 0;
  border-radius: var(--vp-radius);
  margin-bottom: 20px;
  overflow: hidden;
}

.pv-cover {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  z-index: 0;
}

/*
  A photo cover has to keep white text legible, so the navy comes back as a scrim
  that is heaviest where the identity block sits.
*/
.pv-hero.has-cover::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    rgba(11, 29, 56, 0.94) 0%,
    rgba(11, 29, 56, 0.84) 45%,
    rgba(11, 29, 56, 0.62) 100%
  );
  z-index: 1;
}

.pv-hero-inner,
.pv-tabs {
  position: relative;
  z-index: 2;
}

/* Same geometry as the hero's other buttons so the row reads as one set. */
.pv-cover-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: var(--vp-radius-btn);
  padding: 9px 16px;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.pv-cover-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.16);
}

.pv-cover-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pv-cover-btn--icon {
  padding: 9px 12px;
}

.pv-hero-inner {
  display: flex;
  gap: 22px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.pv-logo {
  position: relative;
  flex-shrink: 0;
}

.pv-logo-frame {
  width: 86px;
  height: 86px;
  border-radius: 18px;
  background: #fff;
  object-fit: contain;
  padding: 10px;
  display: block;
  box-shadow: 0 12px 28px rgba(5, 18, 40, 0.35);
}

.pv-logo-frame--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vp-primary);
}

.pv-logo-edit {
  position: absolute;
  right: -6px;
  bottom: -6px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid var(--vp-gray-800);
  background: var(--vp-accent);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.pv-logo-edit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.pv-identity {
  min-width: 0;
  flex: 1 1 380px;
}

.pv-identity-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/*
  The colour is stated rather than inherited from .pv-hero. The global stylesheet sets
  `h1 { color: var(--pc-heading-color) }` — near-black — and a colour set directly on the
  element beats one inherited from an ancestor whatever the specificity, so the company
  name was rendering dark navy on a dark hero and disappearing outright once a light cover
  photo was behind it.
*/
.pv-name {
  margin: 0;
  color: #fff;
  font-size: 1.7rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

/*
  A cover is whatever photo the supplier uploaded — a white studio shot, a busy logo wall.
  The scrim handles most of it, but a hard edge behind the largest text costs nothing and
  keeps the name readable over the bright patches the scrim alone does not tame.
*/
.pv-hero.has-cover .pv-name {
  text-shadow: 0 1px 3px rgba(11, 29, 56, 0.55);
}

.pv-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: var(--vp-radius-pill);
  padding: 4px 11px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.pv-badge--muted {
  background: rgba(255, 255, 255, 0.06);
  color: var(--vp-gray-400);
}

.pv-meta {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 10px;
  font-size: 0.82rem;
  color: #b7c9e2;
}

.pv-meta span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.pv-rating {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.pv-stars {
  display: flex;
  gap: 3px;
  font-size: 1.05rem;
  color: var(--vp-warning);
}

.pv-rating-value {
  font-size: 0.9rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pv-rating-note {
  font-size: 0.8rem;
  color: #b7c9e2;
}

.pv-actions {
  display: flex;
  gap: 9px;
  flex-wrap: wrap;
  padding-top: 4px;
}

.pv-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  margin-top: 22px;
}

.pv-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: rgba(255, 255, 255, 0.07);
  color: #cbd9ec;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 500;
  padding: 12px 18px;
  border-radius: 12px 12px 0 0;
  white-space: nowrap;
  cursor: pointer;
}

.pv-tab:hover {
  background: rgba(255, 255, 255, 0.13);
}

/* The active tab takes the page background so it reads as joined to the content. */
.pv-tab.is-active {
  background: var(--vp-body-bg);
  color: var(--vp-heading-color);
  font-weight: 700;
}

.pv-tab-count {
  border-radius: var(--vp-radius-chip);
  padding: 1px 7px;
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}

.pv-tab.is-active .pv-tab-count {
  background: var(--vp-gray-200);
  color: var(--vp-gray-600);
}
</style>
